import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import StreamIcon from "@mui/icons-material/Stream";
import StopIcon from "@mui/icons-material/Stop";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import useSimApi from "src/hooks/useSimApi";
import useAuth from "src/context/AuthContext/useAuth";
import dayjs from "dayjs";

const METHOD_COLORS: Record<string, "success" | "primary" | "warning" | "info" | "error" | "default"> = {
  GET: "success",
  POST: "primary",
  PUT: "warning",
  PATCH: "info",
  DELETE: "error",
};

function statusCodeColor(
  code: number
): "success" | "info" | "warning" | "error" | "default" {
  if (code >= 200 && code < 300) return "success";
  if (code >= 300 && code < 400) return "info";
  if (code >= 400 && code < 500) return "warning";
  if (code >= 500) return "error";
  return "default";
}

interface LogEntry {
  timestamp: number;
  method: string;
  path: string;
  query: Record<string, string>;
  body: unknown;
  matched: string | null;
  status_code: number;
}

const Logs: React.FC = () => {
  const { request } = useSimApi();
  const { idToken, connectorId } = useAuth();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [streaming, setStreaming] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const esRef = useRef<EventSource | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await request("/api/v1/logs");
      if (res.ok) setLogs(await res.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => { esRef.current?.close(); };
  }, []);

  const startStream = () => {
    if (esRef.current) return;
    // EventSource cannot set custom headers; pass token as query param.
    // The simulator-api derives connector_id from the Firebase identity.
    const url = `/api/v1/logs/stream?_token=${encodeURIComponent(idToken || "")}`;
    const es = new EventSource(url);
    es.onmessage = (e) => {
      if (e.data === "{}") return; // ping
      try {
        const entry = JSON.parse(e.data);
        setLogs((prev) => [entry, ...prev].slice(0, 500));
      } catch {}
    };
    es.onerror = () => stopStream();
    esRef.current = es;
    setStreaming(true);
  };

  const stopStream = () => {
    esRef.current?.close();
    esRef.current = null;
    setStreaming(false);
  };

  return (
    <Box sx={{ p: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>
          Request Logs
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={load} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {streaming ? (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<StopIcon />}
              onClick={stopStream}
              sx={{ textTransform: "none" }}
            >
              Stop stream
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<StreamIcon />}
              onClick={startStream}
              sx={{ textTransform: "none", color: "#7166cf", borderColor: "#7166cf" }}
            >
              Live stream
            </Button>
          )}
          <Tooltip title="Clear view">
            <IconButton size="small" onClick={() => setLogs([])}>
              <DeleteSweepIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {streaming && (
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <CircularProgress size={12} sx={{ color: "#22c55e" }} />
          <Typography variant="caption" color="#22c55e">
            Live — incoming requests appear in real time
          </Typography>
        </Stack>
      )}

      {loading ? (
        <CircularProgress size={24} />
      ) : logs.length === 0 ? (
        <Typography color="text.secondary">No requests logged yet.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {logs.map((entry, idx) => (
            <Box key={idx}>
              <Box
                onClick={() => setExpanded(expanded === idx ? null : idx)}
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#f9fafb" },
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 160 }}>
                  {dayjs(entry.timestamp * 1000).format("HH:mm:ss.SSS")}
                </Typography>
                <Chip
                  label={entry.method}
                  size="small"
                  color={METHOD_COLORS[entry.method] ?? "default"}
                  sx={{ fontWeight: 700, minWidth: 64, justifyContent: "center" }}
                />
                <Typography
                  variant="body2"
                  sx={{ flex: 1, fontFamily: "monospace", fontSize: 13 }}
                >
                  {entry.path}
                  {Object.keys(entry.query || {}).length > 0 &&
                    "?" + new URLSearchParams(entry.query).toString()}
                </Typography>
                <Chip
                  label={entry.status_code}
                  size="small"
                  color={statusCodeColor(entry.status_code)}
                  variant="outlined"
                />
              </Box>
              {expanded === idx && (
                <Box
                  sx={{
                    bgcolor: "#1e1b4b",
                    color: "#e0e7ff",
                    fontFamily: "monospace",
                    fontSize: 12,
                    p: 2,
                    borderRadius: "0 0 4px 4px",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                  }}
                >
                  {JSON.stringify(entry, null, 2)}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Logs;
